import React, { useRef, useEffect, useState } from "react";

interface RetroVideoPlayerProps {
  videoSrc: string;
  name: string;
  visible: boolean;
  anchorRect: DOMRect | null;
}

const RetroVideoPlayer: React.FC<RetroVideoPlayerProps> = ({
  videoSrc,
  name,
  visible,
  anchorRect,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [showContent, setShowContent] = useState(false);
  const [timeCode, setTimeCode] = useState("00:00:00");

  useEffect(() => {
    if (visible && videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(() => {});
      const timer = setTimeout(() => setShowContent(true), 80);
      return () => clearTimeout(timer);
    } else {
      setShowContent(false);
      if (videoRef.current) {
        videoRef.current.pause();
      }
    }
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    const interval = setInterval(() => {
      if (videoRef.current) {
        const t = videoRef.current.currentTime;
        const h = String(Math.floor(t / 3600)).padStart(2, "0");
        const m = String(Math.floor((t % 3600) / 60)).padStart(2, "0");
        const s = String(Math.floor(t % 60)).padStart(2, "0");
        setTimeCode(`${h}:${m}:${s}`);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [visible]);

  if (!anchorRect) return null;

  const top = anchorRect.top - 420;
  const left = anchorRect.left + anchorRect.width / 2 - 280;

  return (
    <div
      className="fixed z-[9999] pointer-events-none"
      style={{
        top: Math.max(8, top),
        left: Math.max(8, left),
        width: 560,
        opacity: visible ? 1 : 0,
        transform: visible && showContent
          ? "scale(1) translateY(0)"
          : "scale(0.7) translateY(12px)",
        transformOrigin: "bottom center",
        transition: "opacity 0.25s cubic-bezier(0.4,0,0.2,1), transform 0.3s cubic-bezier(0.34,1.56,0.64,1)",
      }}
    >
      <div
        style={{
          background: "#1a1a1a",
          borderRadius: 10,
          border: "3px solid #444",
          boxShadow: "0 0 32px rgba(0,255,100,0.15), 0 12px 48px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.08)",
          overflow: "hidden",
          fontFamily: "'Courier New', monospace",
        }}
      >
        {/* Top bar */}
        <div
          style={{
            background: "#2a2a2a",
            padding: "8px 14px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid #333",
          }}
        >
          <div style={{ display: "flex", gap: 6 }}>
            <div style={{ width: 12, height: 12, borderRadius: "50%", background: "#ff5f57" }} />
            <div style={{ width: 12, height: 12, borderRadius: "50%", background: "#febc2e" }} />
            <div style={{ width: 12, height: 12, borderRadius: "50%", background: "#28c840" }} />
          </div>
          <span style={{ color: "#888", fontSize: 13, letterSpacing: 2, textTransform: "uppercase" }}>
            VHS-2026
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div
              style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                background: "#ff3333",
                boxShadow: "0 0 8px #ff3333",
                animation: "retro-blink 1s ease-in-out infinite",
              }}
            />
            <span style={{ color: "#ff3333", fontSize: 12, fontWeight: "bold", letterSpacing: 1.5 }}>REC</span>
          </div>
        </div>

        {/* Video area */}
        <div style={{ position: "relative", background: "#000" }}>
          <video
            ref={videoRef}
            src={videoSrc}
            muted
            loop
            playsInline
            style={{
              width: "100%",
              height: 316,
              objectFit: "cover",
              display: "block",
            }}
          />

          {/* Scanlines overlay */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              background:
                "repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.12) 2px, rgba(0,0,0,0.12) 4px)",
              pointerEvents: "none",
              mixBlendMode: "multiply",
            }}
          />

          {/* CRT vignette */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              background:
                "radial-gradient(ellipse at center, transparent 60%, rgba(0,0,0,0.5) 100%)",
              pointerEvents: "none",
            }}
          />

          {/* VHS tracking noise */}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: "linear-gradient(90deg, transparent 20%, rgba(255,255,255,0.08) 50%, transparent 80%)",
              animation: "retro-tracking 2s linear infinite",
            }}
          />

          {/* Timecode overlay */}
          <div
            style={{
              position: "absolute",
              bottom: 10,
              right: 14,
              color: "#fff",
              fontSize: 16,
              fontFamily: "'Courier New', monospace",
              textShadow: "0 0 6px rgba(0,255,100,0.8), 1px 1px 0 #000",
              letterSpacing: 1.5,
              opacity: 0.9,
            }}
          >
            {timeCode}
          </div>

          {/* PLAY indicator */}
          <div
            style={{
              position: "absolute",
              top: 10,
              left: 14,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <div
              style={{
                width: 0,
                height: 0,
                borderTop: "8px solid transparent",
                borderBottom: "8px solid transparent",
                borderLeft: "13px solid #fff",
                filter: "drop-shadow(0 0 4px rgba(0,255,100,0.6))",
                opacity: 0.9,
              }}
            />
            <span
              style={{
                color: "#fff",
                fontSize: 14,
                fontFamily: "'Courier New', monospace",
                textShadow: "0 0 6px rgba(0,255,100,0.8)",
                letterSpacing: 2,
                opacity: 0.9,
              }}
            >
              PLAY
            </span>
          </div>
        </div>

        {/* Bottom bar */}
        <div
          style={{
            background: "#222",
            padding: "10px 14px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderTop: "1px solid #333",
          }}
        >
          <span
            style={{
              color: "#0f0",
              fontSize: 16,
              fontFamily: "'Courier New', monospace",
              textShadow: "0 0 8px rgba(0,255,0,0.5)",
              letterSpacing: 0.5,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: 320,
            }}
          >
            {name}
          </span>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <span style={{ color: "#666", fontSize: 12, letterSpacing: 1.5 }}>CH-01</span>
            <div
              style={{
                display: "flex",
                gap: 2,
                alignItems: "flex-end",
                height: 16,
              }}
            >
              {[6, 11, 8, 15, 10, 13, 5].map((h, i) => (
                <div
                  key={i}
                  style={{
                    width: 3,
                    height: h,
                    background: i < 4 ? "#0f0" : i < 6 ? "#ff0" : "#f00",
                    opacity: 0.8,
                    animation: `retro-bar-${i % 3} 0.5s ease-in-out infinite alternate`,
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RetroVideoPlayer;
